// @flow
import * as React from 'react';
import { wrapPromise } from 'utils/functional';
import * as localStorage from 'utils/localStorage';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Icon from 'components/Icon';
import Button, { LinkButton } from 'components/Button';
import { H4, BodySmall, H5 } from 'components/Text';
import ShowConfirmationModal from 'containers/ShowConfirmationModal';
import { getRgbaString } from 'utils/style';
import type { Steps } from './Form';
import { colors } from '../../../theme';

const testMode = localStorage.getItem('testMode');

export const BaseHeader = ({
  goBack,
  otherLinks,
  edited,
  title,
  children
}: {
  goBack: Function,
  otherLinks?: Array<React.Node>,
  edited?: boolean,
  title?: string,
  children?: React.Node
}) => (
  <Box
    position={testMode ? 'relative' : 'fixed'}
    top={0}
    left={0}
    width="100%"
    bg="white"
    zIndex="100"
  >
    <Box py={20} borderBottom="1px solid #eaeaea" position="relative" px={32}>
      <Box
        position="absolute"
        top="50%"
        left={0}
        transform="translateY(-50%)"
        width={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box
          borderRight={`1px solid ${colors.aluminum}`}
          width={132}
          height={36}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <ShowConfirmationModal
            data-qa-id="multi-step-back-modal"
            type="danger"
            title={getText('Are you sure you want to go back?')}
            buttonText={getText('Yes, go back')}
            body={getText('If you go back, your data will not be saved.')}
            onConfirm={wrapPromise(goBack)}
            render={({ showModal }) => (
              <LinkButton
                onClick={() => (edited ? showModal() : goBack())}
                data-qa-id="multi-form-back"
              >
                <Box display="flex" alignItems="center">
                  <Icon
                    name="chevron"
                    direction="left"
                    display="block"
                    mt="2px"
                    mr="8px"
                    width="9px"
                    height="5px"
                    fill="currentColor"
                  />
                  <BodySmall>{getText('Cancel')}</BodySmall>
                </Box>
              </LinkButton>
            )}
          />
        </Box>

        {otherLinks && (
          <Hide xs sm md>
            <Box display="flex" justifyContent="center" alignItems="center">
              {otherLinks.map((link, index) => (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  px={32}
                  height={36}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  borderLeft={index > 0 && `1px solid ${colors.aluminum}`}
                >
                  {link}
                </Box>
              ))}
            </Box>
          </Hide>
        )}
      </Box>

      {title && (
        <H4 m={0} textAlign={['right', 'center']}>
          {title}
        </H4>
      )}
    </Box>

    {otherLinks && (
      <Hide lg xl>
        <Box
          display="flex"
          justifyContent={['flex-start', 'center']}
          alignItems="center"
          width={1}
          borderBottom="1px solid #eaeaea"
        >
          {otherLinks.map((link, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              px={32}
              height={36}
              display="flex"
              justifyContent="center"
              alignItems="center"
              border={index > 0 && `1px solid ${colors.aluminum}`}
            >
              {link}
            </Box>
          ))}
        </Box>
      </Hide>
    )}

    {children}
  </Box>
);

export type HeaderProps = {
  steps: Steps,
  currentStep: number,
  goBack: Function,
  goBackToStep: Function,
  errors: Array<Array<string>>,
  title?: string,
  edited: boolean
};

const Header = ({
  steps,
  currentStep,
  goBack,
  goBackToStep,
  errors,
  title,
  edited
}: HeaderProps) => (
  <BaseHeader goBack={goBack} edited={edited} title={title}>
    <Box display="flex">
      {steps.map((step, index) => {
        const hasError = errors && errors[index] && errors[index].length > 0;
        return (
          <Box
            key={step.title}
            width={1 / steps.length}
            height={50}
            color={
              hasError
                ? colors.scarlet
                : index === currentStep
                ? colors.waterBlue
                : index < currentStep
                ? colors.greyishBrown
                : colors.coolGrey
            }
            boxShadow={`0 4px ${
              hasError
                ? colors.scarlet
                : index === currentStep
                ? colors.waterBlue
                : index < currentStep
                ? `rgba(${getRgbaString(colors.waterBlue, 0.7)})`
                : colors.aluminum
            }`}
            borderLeft={index > 0 && '1px solid #eaeaea'}
          >
            <Box
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              component={Button}
              onClick={() => goBackToStep(index)}
              disabled={index >= currentStep}
            >
              <H5 textAlign="center" m={0} fontSize={[14, 14, 14, 14]}>
                {index + 1}. {step.title}
              </H5>
            </Box>
          </Box>
        );
      })}
    </Box>
  </BaseHeader>
);

export default Header;
